import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import NavbarSection from "../../components/pages/home/navbar-section";
import PageMeta from "../../components/common/page-meta";
import { AuthenticationContext } from "../../context/authentication-context";
import {
  deleteThought,
  saveThought,
  subscribeToAdminThoughts,
  uploadThoughtImage,
} from "../../data/firebase";
import { ThoughtPost } from "../../data/thoughts/props";
import {
  EditorButton,
  EditorDeleteButton,
  EditorField,
  EditorFileInput,
  EditorHeader,
  EditorHelp,
  EditorInput,
  EditorKicker,
  EditorLayout,
  EditorLibrary,
  EditorLibraryEmpty,
  EditorLibraryGroup,
  EditorLibraryHeading,
  EditorLibraryItem,
  EditorMain,
  EditorImagePreview,
  EditorNewButton,
  EditorPage,
  EditorRemoveImage,
  EditorSidebar,
  EditorSidebarTitle,
  EditorStatus,
  EditorTextarea,
  EditorTitle,
} from "./style";

const ThoughtEditor = () => {
  const history = useHistory();
  const location = useLocation();
  const { isLoggedIn, isAuthenticationReady } = useContext(AuthenticationContext);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [hasError, setHasError] = useState(false);
  const [editingThought, setEditingThought] = useState<ThoughtPost | null>(null);
  const [savedThoughts, setSavedThoughts] = useState<ThoughtPost[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [libraryError, setLibraryError] = useState(false);

  useEffect(() => {
    if (isAuthenticationReady && !isLoggedIn) {
      history.replace(
        `/login?redirectUrl=${encodeURIComponent(location.pathname)}`
      );
    }
  }, [history, isAuthenticationReady, isLoggedIn, location.pathname]);

  useEffect(() => {
    return () => {
      if (imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  useEffect(() => {
    if (!isLoggedIn) return;

    return subscribeToAdminThoughts(
      (thoughts) => {
        setSavedThoughts(thoughts);
        setLibraryLoading(false);
        setLibraryError(false);
      },
      () => {
        setLibraryLoading(false);
        setLibraryError(true);
      }
    );
  }, [isLoggedIn]);

  if (!isAuthenticationReady || !isLoggedIn) {
    return null;
  }

  const selectImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith("image/") || file.size > 5 * 1024 * 1024) {
      setHasError(true);
      setStatus("Choose a JPG, PNG, GIF, or WebP image smaller than 5 MB.");
      event.target.value = "";
      return;
    }
    setHasError(false);
    setStatus("");
    setImage(file);
    setImageUrl("");
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImage(null);
    setImageUrl("");
    setImagePreview("");
  };

  const resetEditor = () => {
    setEditingThought(null);
    setTitle("");
    setDescription("");
    setBody("");
    setImage(null);
    setImageUrl("");
    setImagePreview("");
    setHasError(false);
    setStatus("");
  };

  const hasUnsavedChanges = () => {
    if (!editingThought) {
      return Boolean(
        title.trim() || description.trim() || body.trim() || image || imageUrl
      );
    }

    return Boolean(
      title !== editingThought.title ||
        description !== editingThought.description ||
        body !== editingThought.body ||
        image ||
        imageUrl !== editingThought.imageUrl
    );
  };

  const startNewThought = () => {
    if (
      hasUnsavedChanges() &&
      !window.confirm("Start a new thought? Any unsaved changes will be lost.")
    ) {
      return;
    }
    resetEditor();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openThought = (thought: ThoughtPost) => {
    if (
      editingThought?.slug !== thought.slug &&
      hasUnsavedChanges() &&
      !window.confirm("Open another writing? Any unsaved changes will be lost.")
    ) {
      return;
    }

    setEditingThought(thought);
    setTitle(thought.title);
    setDescription(thought.description);
    setBody(thought.body);
    setImage(null);
    setImageUrl(thought.imageUrl);
    setImagePreview(thought.imageUrl);
    setHasError(false);
    setStatus(thought.published ? "Editing published thought." : "Draft opened.");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (published: boolean) => {
    if (!title.trim()) {
      setHasError(true);
      setStatus("Add a title before saving this draft.");
      return;
    }
    if (published && (!description.trim() || !body.trim())) {
      setHasError(true);
      setStatus("Add a little description and your writing before publishing.");
      return;
    }

    setSaving(true);
    setHasError(false);
    setStatus(published ? "Publishing…" : "Saving draft…");
    try {
      let uploadedImageUrl = imageUrl;
      if (image) {
        setStatus("Uploading image…");
        uploadedImageUrl = await uploadThoughtImage(image);
        setImageUrl(uploadedImageUrl);
        setImage(null);
        setImagePreview(uploadedImageUrl);
      }
      const thought = await saveThought(
        {
          title: title.trim(),
          description: description.trim(),
          body: body.trim(),
          imageUrl: uploadedImageUrl,
          published,
        },
        editingThought
          ? {
              slug: editingThought.slug,
              publishedAt: editingThought.publishedAt,
            }
          : undefined
      );
      setEditingThought(thought);
      if (published) {
        history.push(`/thoughts/${thought.slug}`);
        return;
      }
      setStatus("Draft saved.");
    } catch (error: any) {
      setHasError(true);
      if (error?.code === "permission-denied") {
        setStatus("Firebase blocked this save. Please update the Firestore rules for the signed-in admin account.");
      } else if (error?.code?.startsWith("storage/")) {
        setStatus("The image could not be uploaded. Remove it and publish the text, or check Firebase Storage.");
      } else {
        setStatus("The writing could not be saved. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const removeThought = async () => {
    if (
      !editingThought ||
      !window.confirm(`Delete “${editingThought.title}”? This cannot be undone.`)
    ) {
      return;
    }

    setSaving(true);
    setHasError(false);
    setStatus("Deleting…");
    try {
      await deleteThought(editingThought.slug);
      resetEditor();
      setStatus("Writing deleted.");
    } catch (error: any) {
      setHasError(true);
      setStatus(
        error?.code === "permission-denied"
          ? "Firebase blocked this deletion."
          : "The writing could not be deleted. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const drafts = savedThoughts.filter((thought) => !thought.published);
  const publishedThoughts = savedThoughts.filter((thought) => thought.published);

  const formatUpdatedAt = (value: string) => {
    if (!value) return "Saved";
    return new Intl.DateTimeFormat("en", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  };

  return (
    <EditorPage>
      <PageMeta
        title="Write a thought — Freddy's"
        description="Private writing space for Freddy's portfolio journal."
      />
      <NavbarSection percentage={0} showLine={false} />
      <EditorLayout>
        <EditorMain>
          <EditorKicker>Private admin · your words only</EditorKicker>
          <EditorHeader>
            <EditorTitle>{editingThought ? "Edit thought" : "Write a thought"}</EditorTitle>
            <EditorNewButton type="button" onClick={startNewThought}>
              + New thought
            </EditorNewButton>
          </EditorHeader>
          <EditorField>
            <span>Title</span>
            <EditorInput
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="A thought I want to keep"
            />
          </EditorField>
          <EditorField>
            <span>Little description</span>
            <EditorInput
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="One sentence that invites people in"
            />
          </EditorField>
          <EditorField>
            <span>Writing</span>
            <EditorTextarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Start writing here. Leave a blank line between paragraphs."
            />
          </EditorField>
          <EditorField>
            <span>Cover image (optional)</span>
            <EditorFileInput
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={selectImage}
            />
            {imagePreview && (
              <>
                <EditorImagePreview src={imagePreview} alt="Image preview" />
                <EditorRemoveImage type="button" onClick={removeImage}>
                  Remove image
                </EditorRemoveImage>
              </>
            )}
          </EditorField>
        </EditorMain>
        <EditorSidebar>
          <EditorSidebarTitle>
            {editingThought?.published ? "Published" : "Draft"}
          </EditorSidebarTitle>
          <EditorHelp>
            Drafts stay hidden. Published thoughts appear above the Medium links
            and open in the quiet reading view. Images can be up to 5 MB.
          </EditorHelp>
          <EditorButton
            type="button"
            disabled={saving}
            onClick={() => submit(false)}
          >
            {editingThought?.published ? "Move to drafts" : "Save draft"}
          </EditorButton>
          <EditorButton
            type="button"
            primary
            disabled={saving}
            onClick={() => submit(true)}
          >
            {editingThought?.published ? "Update published thought" : "Publish thought"}
          </EditorButton>
          {editingThought && (
            <EditorDeleteButton type="button" disabled={saving} onClick={removeThought}>
              Delete writing
            </EditorDeleteButton>
          )}
          <EditorStatus error={hasError} aria-live="polite">
            {status}
          </EditorStatus>
          <EditorLibrary>
            <EditorSidebarTitle>Your writing</EditorSidebarTitle>
            <EditorHelp>
              Open a saved draft to continue writing or update something already
              published.
            </EditorHelp>
            {libraryLoading && (
              <EditorLibraryEmpty>Loading your writing…</EditorLibraryEmpty>
            )}
            {libraryError && (
              <EditorLibraryEmpty>
                Your saved writing could not be loaded.
              </EditorLibraryEmpty>
            )}
            {!libraryLoading && !libraryError && (
              <>
                <EditorLibraryGroup>
                  <EditorLibraryHeading>Drafts · {drafts.length}</EditorLibraryHeading>
                  {drafts.length === 0 && (
                    <EditorLibraryEmpty>No drafts yet.</EditorLibraryEmpty>
                  )}
                  {drafts.map((thought) => (
                    <EditorLibraryItem
                      type="button"
                      key={thought.slug}
                      $active={editingThought?.slug === thought.slug}
                      onClick={() => openThought(thought)}
                    >
                      <strong>{thought.title || "Untitled draft"}</strong>
                      <span>{formatUpdatedAt(thought.updatedAt)}</span>
                    </EditorLibraryItem>
                  ))}
                </EditorLibraryGroup>
                <EditorLibraryGroup>
                  <EditorLibraryHeading>
                    Published · {publishedThoughts.length}
                  </EditorLibraryHeading>
                  {publishedThoughts.length === 0 && (
                    <EditorLibraryEmpty>No published thoughts yet.</EditorLibraryEmpty>
                  )}
                  {publishedThoughts.map((thought) => (
                    <EditorLibraryItem
                      type="button"
                      key={thought.slug}
                      $active={editingThought?.slug === thought.slug}
                      onClick={() => openThought(thought)}
                    >
                      <strong>{thought.title}</strong>
                      <span>{formatUpdatedAt(thought.updatedAt)}</span>
                    </EditorLibraryItem>
                  ))}
                </EditorLibraryGroup>
              </>
            )}
          </EditorLibrary>
        </EditorSidebar>
      </EditorLayout>
    </EditorPage>
  );
};

export default ThoughtEditor;
